'use client'

/**
 * Tailwind-v4 rich text editor.
 *
 * Built on TipTap. Every surface is token-driven (`border-border`,
 * `text-muted-foreground`, `bg-foreground/N`), lucide icons, and the same
 * hover/active progression as the package's Button. No hardcoded neutral
 * ever: an opaque grey can't tint, so it reads as a cold patch on the warm
 * stone canvas — alpha over `--foreground` follows whatever it sits on.
 *
 * Returns HTML via `onChange(html)`. Apps can pass HTML back in via
 * `value`; the editor diffs against `editor.getHTML()` so resetting
 * to the same string is a no-op.
 */
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react'
import {
  Bold as BoldIcon,
  Braces,
  Heading1,
  Heading2,
  Heading3,
  Italic as ItalicIcon,
  Link2 as LinkIcon,
  List as BulletListIcon,
  ListOrdered as OrderedListIcon,
  Quote as BlockquoteIcon,
  Strikethrough as StrikeIcon,
  Underline as UnderlineIcon,
  X,
} from 'lucide-react'
import { EditorContent, useEditor, type Editor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Placeholder from '@tiptap/extension-placeholder'
import Link from '@tiptap/extension-link'

import { cn } from '../lib/utils'
import { Button } from './button'
import { MergeTagNode } from './merge-tag-node'
import { MergeTagPicker } from './merge-tag-picker'
import {
  mergeTagKey,
  type MergeTagRegistry,
} from '../blocks/email-builder/merge-tags'

export interface RichTextEditorProps {
  value?: string
  onChange?: (html: string) => void
  placeholder?: string
  className?: string
  disabled?: boolean
  /**
   * Hide specific toolbar groups when the surface doesn't need them
   * — e.g. lists are usually irrelevant inside a heading-only block.
   */
  toolbar?: {
    headings?: boolean
    inline?: boolean
    lists?: boolean
    quote?: boolean
    link?: boolean
    /**
     * "Insert merge tag" — only renders when a non-empty
     * `mergeTags` registry is also passed.
     */
    mergeTags?: boolean
  }
  /**
   * Optional merge-tag registry. When supplied (and the toolbar
   * isn't told to hide it), the editor registers an inline atom
   * node for chips and shows an "Insert merge tag" button.
   * Editor `getHTML()` serialises chips to
   * `<span data-mt="…">{{ … }}</span>` so the canonical
   * placeholder survives in the saved HTML.
   */
  mergeTags?: MergeTagRegistry
  /**
   * Extra toolbar elements rendered at the end of the toolbar (e.g. an
   * app-specific asset or template picker). Pair with the editor ref's
   * `insertContent` to insert at the cursor.
   */
  toolbarActions?: React.ReactNode
  /**
   * Slot rendered between the toolbar and the editing surface — e.g. a row of
   * attached-asset badges. Spans the full width under the toolbar.
   */
  belowToolbar?: React.ReactNode
  /**
   * When true, clicking a link inside the editor opens it (new tab) instead of
   * just placing the caret. Off by default so link editing isn't hijacked.
   */
  linkOpenOnClick?: boolean
  /**
   * Remove headings from the schema entirely (not just the toolbar buttons):
   * loaded/pasted h1–h6 and `#` markdown shortcuts all normalise to
   * paragraphs. Use for textarea-grade surfaces where a stray heading would
   * be uneditable noise.
   */
  allowHeadings?: boolean
  /**
   * Plain-text mode: no toolbar, no marks/nodes in the schema (pasted
   * rich content and markdown shortcuts normalise to plain paragraphs),
   * and `onChange` receives `editor.getText()` instead of HTML. Use for
   * channels that only accept plain text (e.g. SMS-grade replies).
   */
  plainText?: boolean
}

/** Imperative handle for inserting content at the cursor from outside. */
export interface RichTextEditorHandle {
  insertContent: (html: string) => void
  focus: () => void
  /** Delete the first link whose href matches (removes its text too). */
  removeLinkByHref: (href: string) => void
  /** Rename the visible text of the first link with this href; href is kept. */
  setLinkTextByHref: (href: string, text: string) => void
}

const DEFAULT_TOOLBAR: Required<NonNullable<RichTextEditorProps['toolbar']>> = {
  headings: true,
  inline: true,
  lists: true,
  quote: true,
  link: true,
  mergeTags: true,
}

/** Escape a plain string and wrap its lines in paragraphs for setContent. */
function textToParagraphs(text: string): string {
  if (text === '') return ''
  return text
    .split('\n')
    .map((line) => {
      const escaped = line
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
      return `<p>${escaped}</p>`
    })
    .join('')
}

/** Find the document range + mark attrs of the first link with `href`. */
function findLinkRange(
  editor: Editor,
  href: string
): { from: number; to: number; attrs: Record<string, unknown> } | null {
  let result: {
    from: number
    to: number
    attrs: Record<string, unknown>
  } | null = null
  editor.state.doc.descendants((node, pos) => {
    if (result) return false
    if (!node.isText) return true
    const link = node.marks.find(
      (m) => m.type.name === 'link' && m.attrs.href === href
    )
    if (link) {
      result = { from: pos, to: pos + node.nodeSize, attrs: link.attrs }
      return false
    }
    return true
  })
  return result
}

export const RichTextEditor = forwardRef<
  RichTextEditorHandle,
  RichTextEditorProps
>(function RichTextEditor(
  {
    value = '',
    onChange,
    placeholder = 'Write something…',
    className,
    disabled = false,
    toolbar,
    mergeTags,
    toolbarActions,
    belowToolbar,
    linkOpenOnClick = false,
    allowHeadings = true,
    plainText = false,
  },
  ref
) {
  const tb = { ...DEFAULT_TOOLBAR, ...(toolbar ?? {}) }
  const hasMergeTags = !plainText && !!mergeTags && mergeTags.length > 0

  const editor = useEditor({
    extensions: [
      StarterKit.configure(
        plainText
          ? {
              // Plain-text schema: paragraphs + hard breaks only. Every
              // mark/node is stripped so pasted rich content and markdown
              // shortcuts (`**`, `#`, `-`) normalise to plain text.
              heading: false,
              bold: false,
              italic: false,
              strike: false,
              code: false,
              codeBlock: false,
              blockquote: false,
              bulletList: false,
              orderedList: false,
              listItem: false,
              horizontalRule: false,
              underline: false,
              link: false,
            }
          : {
              bulletList: { keepMarks: true, keepAttributes: false },
              orderedList: { keepMarks: true, keepAttributes: false },
              ...(allowHeadings ? {} : { heading: false }),
            }
      ),
      Placeholder.configure({ placeholder }),
      // Preserve `data-asset` / `data-mime` / `data-filename` on links so an
      // app can mark library-asset links and re-read them from `getHTML()`
      // (the stock Link mark only keeps href/rel/target and drops data-*).
      // Skipped entirely in plain-text mode.
      ...(plainText
        ? []
        : [
            Link.extend({
              addAttributes() {
                return {
                  ...this.parent?.(),
                  dataAsset: {
                    default: null,
                    parseHTML: (el: HTMLElement) =>
                      el.hasAttribute('data-asset') ? '' : null,
                    renderHTML: (attrs: Record<string, unknown>) =>
                      attrs.dataAsset != null ? { 'data-asset': '' } : {},
                  },
                  dataMime: {
                    default: null,
                    parseHTML: (el: HTMLElement) =>
                      el.getAttribute('data-mime'),
                    renderHTML: (attrs: Record<string, unknown>) =>
                      attrs.dataMime ? { 'data-mime': attrs.dataMime } : {},
                  },
                  dataFilename: {
                    default: null,
                    parseHTML: (el: HTMLElement) =>
                      el.getAttribute('data-filename'),
                    renderHTML: (attrs: Record<string, unknown>) =>
                      attrs.dataFilename
                        ? { 'data-filename': attrs.dataFilename }
                        : {},
                  },
                }
              },
            }).configure({
              openOnClick: linkOpenOnClick,
              HTMLAttributes: {
                rel: 'noopener noreferrer',
                target: '_blank',
              },
            }),
          ]),
      // Always register the chip node when ANY merge-tag registry
      // is supplied — without it, saved docs containing chip
      // spans would be silently dropped on load.
      ...(hasMergeTags ? [MergeTagNode] : []),
    ],
    content: plainText ? textToParagraphs(value) : value,
    editable: !disabled,
    // SSR consumers (Next apps) must defer the first render to the
    // client or TipTap throws a hydration error; no-op for SPAs.
    immediatelyRender: false,
    onUpdate: ({ editor }) => {
      onChange?.(plainText ? editor.getText() : editor.getHTML())
    },
  })

  // Push external `value` changes back into the editor without
  // creating a feedback loop with `onUpdate`.
  useEffect(() => {
    if (!editor) return
    const current = plainText ? editor.getText() : editor.getHTML()
    if (value !== current) {
      editor.commands.setContent(plainText ? textToParagraphs(value) : value, {
        emitUpdate: false,
      })
    }
  }, [value, editor, plainText])

  useEffect(() => {
    editor?.setEditable(!disabled)
  }, [disabled, editor])

  useImperativeHandle(
    ref,
    () => ({
      insertContent: (html: string) => {
        editor?.chain().focus().insertContent(html).run()
      },
      focus: () => {
        editor?.chain().focus().run()
      },
      removeLinkByHref: (href: string) => {
        if (!editor) return
        const range = findLinkRange(editor, href)
        if (range) editor.chain().focus().deleteRange(range).run()
      },
      setLinkTextByHref: (href: string, text: string) => {
        if (!editor) return
        const range = findLinkRange(editor, href)
        if (!range) return
        editor
          .chain()
          .focus()
          .insertContentAt(range, {
            type: 'text',
            text,
            marks: [{ type: 'link', attrs: range.attrs }],
          })
          .run()
      },
    }),
    [editor]
  )

  if (!editor) return null

  return (
    <div
      className={cn(
        // Mirrors `<Textarea>`: the single `--border` token and `rounded-lg`,
        // so a form mixing a textarea and a rich-text field reads as one
        // control family. Focus shifts the border rather than drawing
        // Textarea's outset ring — the inbox send box passes `border-0` to sit
        // chrome-less inside its own container, and a ring would put the
        // chrome straight back.
        'rounded-lg border border-border bg-transparent transition-colors focus-within:border-ring',
        disabled && 'pointer-events-none opacity-60',
        className
      )}
    >
      {plainText ? null : (
        <Toolbar
          editor={editor}
          disabled={disabled}
          toolbar={tb}
          mergeTags={mergeTags}
          toolbarActions={toolbarActions}
        />
      )}
      {belowToolbar}
      <EditorContent
        editor={editor}
        className={cn(
          'prose prose-sm max-w-none px-3 py-2 dark:prose-invert',
          '[&_.ProseMirror]:min-h-[80px] [&_.ProseMirror]:outline-none',
          // Explicit list markers + indentation. The editor relies on `prose`
          // for these, but a host app's CSS reset (or no typography plugin)
          // can strip `list-style`, leaving toggled lists with no bullets /
          // numbers. These descendant rules are specific enough to win.
          '[&_.ProseMirror_ul]:list-disc [&_.ProseMirror_ul]:pl-5 [&_.ProseMirror_ol]:list-decimal [&_.ProseMirror_ol]:pl-5 [&_.ProseMirror_li]:my-0.5 [&_.ProseMirror_li>p]:my-0',
          // Headings + blockquote, again explicit so they don't depend on
          // `prose` being active in the host app.
          '[&_.ProseMirror_h1]:text-xl [&_.ProseMirror_h1]:font-bold [&_.ProseMirror_h2]:text-lg [&_.ProseMirror_h2]:font-semibold [&_.ProseMirror_h3]:text-base [&_.ProseMirror_h3]:font-semibold',
          // Alpha over `--foreground` + the muted token, so both flip with the
          // theme on their own — no `dark:` twin to keep in sync.
          '[&_.ProseMirror_blockquote]:border-l-2 [&_.ProseMirror_blockquote]:border-foreground/20 [&_.ProseMirror_blockquote]:pl-3 [&_.ProseMirror_blockquote]:italic [&_.ProseMirror_blockquote]:text-muted-foreground',
          '[&_.ProseMirror_a]:text-blue-600 [&_.ProseMirror_a]:underline dark:[&_.ProseMirror_a]:text-blue-400',
          // Placeholder styling — TipTap renders an empty <p> with
          // is-editor-empty when the doc is blank; we surface
          // `data-placeholder` via ::before.
          // `foreground/50` is exactly what `<Input>`/`<Textarea>` use for
          // `placeholder:`, so an empty rich-text field greys out to the same
          // weight as an empty text field beside it.
          '[&_.ProseMirror_p.is-editor-empty:first-child]:before:pointer-events-none [&_.ProseMirror_p.is-editor-empty:first-child]:before:float-left [&_.ProseMirror_p.is-editor-empty:first-child]:before:h-0 [&_.ProseMirror_p.is-editor-empty:first-child]:before:text-foreground/50 [&_.ProseMirror_p.is-editor-empty:first-child]:before:content-[attr(data-placeholder)]',
          // Merge-tag chips. Render as a soft pill with the tag
          // label inside, hiding the literal `{{ x.y }}` inner
          // text by sizing it to 0 and using the data-label
          // attribute via ::before. The literal text still
          // serialises out via getHTML, just isn't visible.
          '[&_.mt-chip]:inline-flex [&_.mt-chip]:items-center [&_.mt-chip]:rounded [&_.mt-chip]:bg-foreground/10 [&_.mt-chip]:px-1.5 [&_.mt-chip]:py-0.5 [&_.mt-chip]:text-[0.85em] [&_.mt-chip]:font-medium [&_.mt-chip]:font-mono [&_.mt-chip]:leading-none [&_.mt-chip]:text-foreground [&_.mt-chip]:select-all'
        )}
      />
    </div>
  )
})

function Toolbar({
  editor,
  disabled,
  toolbar,
  mergeTags,
  toolbarActions,
}: {
  editor: Editor
  disabled: boolean
  toolbar: Required<NonNullable<RichTextEditorProps['toolbar']>>
  mergeTags?: MergeTagRegistry
  toolbarActions?: React.ReactNode
}) {
  return (
    <div className="flex flex-wrap items-center gap-0.5 px-1.5 py-1">
      {toolbar.headings ? (
        <ToolbarGroup>
          <ToolbarBtn
            disabled={disabled}
            active={editor.isActive('heading', { level: 1 })}
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 1 }).run()
            }
            label="Heading 1"
          >
            <Heading1 className="size-4" />
          </ToolbarBtn>
          <ToolbarBtn
            disabled={disabled}
            active={editor.isActive('heading', { level: 2 })}
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 2 }).run()
            }
            label="Heading 2"
          >
            <Heading2 className="size-4" />
          </ToolbarBtn>
          <ToolbarBtn
            disabled={disabled}
            active={editor.isActive('heading', { level: 3 })}
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 3 }).run()
            }
            label="Heading 3"
          >
            <Heading3 className="size-4" />
          </ToolbarBtn>
        </ToolbarGroup>
      ) : null}

      {toolbar.inline ? (
        <ToolbarGroup>
          <ToolbarBtn
            disabled={disabled}
            active={editor.isActive('bold')}
            onClick={() => editor.chain().focus().toggleBold().run()}
            label="Bold"
          >
            <BoldIcon className="size-4" />
          </ToolbarBtn>
          <ToolbarBtn
            disabled={disabled}
            active={editor.isActive('italic')}
            onClick={() => editor.chain().focus().toggleItalic().run()}
            label="Italic"
          >
            <ItalicIcon className="size-4" />
          </ToolbarBtn>
          <ToolbarBtn
            disabled={disabled}
            active={editor.isActive('underline')}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            label="Underline"
          >
            <UnderlineIcon className="size-4" />
          </ToolbarBtn>
          <ToolbarBtn
            disabled={disabled}
            active={editor.isActive('strike')}
            onClick={() => editor.chain().focus().toggleStrike().run()}
            label="Strikethrough"
          >
            <StrikeIcon className="size-4" />
          </ToolbarBtn>
        </ToolbarGroup>
      ) : null}

      {toolbar.lists ? (
        <ToolbarGroup>
          <ToolbarBtn
            disabled={disabled}
            active={editor.isActive('bulletList')}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            label="Bullet list"
          >
            <BulletListIcon className="size-4" />
          </ToolbarBtn>
          <ToolbarBtn
            disabled={disabled}
            active={editor.isActive('orderedList')}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            label="Numbered list"
          >
            <OrderedListIcon className="size-4" />
          </ToolbarBtn>
        </ToolbarGroup>
      ) : null}

      {toolbar.quote ? (
        <ToolbarGroup>
          <ToolbarBtn
            disabled={disabled}
            active={editor.isActive('blockquote')}
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            label="Quote"
          >
            <BlockquoteIcon className="size-4" />
          </ToolbarBtn>
        </ToolbarGroup>
      ) : null}

      {toolbar.link ? <LinkButton editor={editor} disabled={disabled} /> : null}
      {toolbar.mergeTags && mergeTags && mergeTags.length > 0 ? (
        <ToolbarGroup>
          <MergeTagPicker
            registry={mergeTags}
            onPick={(tag) => {
              editor
                .chain()
                .focus()
                .insertMergeTag({
                  value: tag.value,
                  key: mergeTagKey(tag.value),
                  label: tag.name,
                })
                .run()
            }}
          >
            {/* Inline a plain <button> here (instead of
                ToolbarBtn) because base-ui's PopoverTrigger
                clones / wraps the trigger element and needs the
                ref + handlers to forward straight through. */}
            <button
              type="button"
              title="Insert merge tag"
              aria-label="Insert merge tag"
              disabled={disabled}
              className={cn(
                'flex size-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-foreground/5 hover:text-foreground disabled:pointer-events-none disabled:opacity-50'
              )}
            >
              <Braces className="size-4" />
            </button>
          </MergeTagPicker>
        </ToolbarGroup>
      ) : null}
      {toolbarActions ? <ToolbarGroup>{toolbarActions}</ToolbarGroup> : null}
    </div>
  )
}

function ToolbarGroup({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-0.5 border-r border-border pr-1 last:border-r-0 last:pr-0">
      {children}
    </div>
  )
}

function ToolbarBtn({
  active,
  disabled,
  onClick,
  label,
  children,
}: {
  active?: boolean
  disabled?: boolean
  onClick: () => void
  label: string
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={active}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        // `rounded-md` (not `rounded`): nested radius steps down from the
        // root's `rounded-lg` by the toolbar's padding, rather than dropping
        // to a near-square corner inside a 12px shell.
        'flex size-7 items-center justify-center rounded-md transition-colors disabled:pointer-events-none disabled:opacity-50',
        active
          ? 'bg-foreground/10 text-foreground'
          : 'text-muted-foreground hover:bg-foreground/5 hover:text-foreground'
      )}
    >
      {children}
    </button>
  )
}

function LinkButton({
  editor,
  disabled,
}: {
  editor: Editor
  disabled: boolean
}) {
  const [open, setOpen] = useState(false)
  const [url, setUrl] = useState('')

  const openInput = useCallback(() => {
    const previous =
      (editor.getAttributes('link').href as string | undefined) ?? ''
    setUrl(previous)
    setOpen(true)
  }, [editor])

  const apply = useCallback(() => {
    if (url.trim() === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run()
    } else {
      const href = /^https?:\/\//i.test(url) ? url : `https://${url}`
      editor.chain().focus().extendMarkRange('link').setLink({ href }).run()
    }
    setOpen(false)
    setUrl('')
  }, [editor, url])

  const cancel = useCallback(() => {
    setOpen(false)
    setUrl('')
  }, [])

  return (
    <ToolbarGroup>
      <ToolbarBtn
        disabled={disabled}
        active={editor.isActive('link')}
        onClick={openInput}
        label="Link"
      >
        <LinkIcon className="size-4" />
      </ToolbarBtn>
      {open ? (
        // Every control on this row is h-7, matching the toolbar buttons it
        // sits beside, so the strip keeps one baseline.
        <div className="flex items-center gap-1.5 px-2">
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                apply()
              } else if (e.key === 'Escape') {
                cancel()
              }
            }}
            className="h-7 w-40 rounded-md border border-border bg-transparent px-2 text-xs outline-none transition-colors placeholder:text-foreground/50 focus:border-ring"
          />
          {/* `type="button"` is load-bearing: `Button` has no type default, and
              this strip renders inside the inbox send box's <form> — an
              untyped button would submit the message instead of setting a
              link. */}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="font-medium"
            onClick={apply}
          >
            {url ? 'Apply' : 'Remove'}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label="Cancel"
            onClick={cancel}
          >
            <X />
          </Button>
        </div>
      ) : null}
    </ToolbarGroup>
  )
}
